import React from 'react';
import { Box, Typography, Button, useMediaQuery, Stack } from '@mui/material';
import { styled, useTheme } from "@mui/material/styles";
import REST from "../../libs/API";
import PSIcon from "../../components/PSIcon";
import { CopyIcon, Warning, ArrowDownIII } from "../../assets/images/icons";
import CClipboard from '../../components/CClipboard';
import Notice from '../main/Notice';
import { getText, formatCardNumber, getCardType } from '../../libs/utils';
import TimerTile from './TimerTile';
import CancelOrder from './CancelOrder';
import { JivoChatButton } from '../../JivoChat';

const MyBox = styled(Box)(({ theme, disabled }) => ({
  boxShadow: '0px 5px 6px -7px rgba(0, 0, 0, 0.35), 0px 4px 14px -3px rgba(0, 0, 0, 0.15)',
  borderRadius: 36,
  zIndex: 1,
  display: 'flex',
  flexWrap: 'wrap',
  //justifyContent: 'space-between',
  padding: '22px 30px 21px 30px',
  [theme.breakpoints.only('xs')]: {
    borderRadius: '16px',
    padding: '19px 15px 18px 15px',
    "& > button": {
      marginLeft: 'none',
      width: '100%',
      marginTop: 16,
    },
  },
  "& > button": {
    height: 'fit-content',
    alignSelf: 'flex-end',
    marginLeft: 'auto',
  },
  ...(disabled && {
    "& > *": {
      filter: 'grayscale(1)  sepia(1) opacity(0.4) hue-rotate(151deg)',
    },
    "& > button, > *:last-child": {
      filter: 'none',
    },
    pointerEvents: 'none'
  }),
}))

const CardInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.only('xs')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'start'
  }
}))

const BottomAction = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  marginTop: '20px',
  flexWrap: 'wrap',
  ":empty": {
    display: 'none'
  },
  [theme.breakpoints.only('xs')]: {
    gap: '15px',
    " > .MuiButton-root": {
      width: '100%',
    },
  }
}))

const DepositPayButtonStyled = styled(Button)(({ theme, statusColor }) => ({
  padding: '0px 18px !important',
  lineHeight: '16px !important',
  display: 'flex !important',
  borderRadius: '12px',
  minWidth: '180px !important',
  width: '100%',
  minHeight: '48px !important',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '145px !important',
    marginTop: '0',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: '0px',
    gridRow: '4 / 4',
    fontSize: '13px'
  },
  margin: 0,
  height: '48px',
  alignSelf: 'center !important',
  ...(statusColor) && {
    pointerEvents: 'none',
    backgroundColor: '#ffffff !important',
    border: '1px solid #fa8282 !important',
    color: '#fa8282 !important',
    marginTop: '8px !important',
  },
}))

const DepositPayButton = ({
  hash,
  canSetPaid,
  canUploadReceipt,
  setPaid,
}) => {
  const [receiptHash, setReceiptHash] = React.useState(null);
  const [receiptStatus, setReceiptStatus] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const statuses = {
    process: getText('receiptUploaded'),
    success: getText('receiptSuccess'),
    error: getText('receiptError'),
  }

  const getReceiptStatus = React.useCallback(() => {
    if (!canUploadReceipt || !receiptHash) return;
    REST.getReceiptStatus(receiptHash).then(data => {
      console.log('receipt status', data);
      if (data) {
        setReceiptStatus(data.data.status);
        if (data.data.status === 'success' || data.data.status === 'error') {
          //setReceiptHash(null);
        }
        if (data.data.status === 'success') {
          if (setPaidRef.current) setPaidRef.current();
        }
      }
    });
  }, [canUploadReceipt, receiptHash]);

  // keep stable ref to latest setPaid to avoid recreating callback every render
  const setPaidRef = React.useRef(setPaid);
  React.useEffect(() => {
    setPaidRef.current = setPaid;
  }, [setPaid]);

  React.useEffect(() => {
    let interval;
    if (canUploadReceipt && receiptHash) {
      getReceiptStatus();
      interval = setInterval(getReceiptStatus, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    }
  }, [canUploadReceipt, getReceiptStatus, receiptHash]);

  const openReceiptUpload = (hash) => {
    const input = document.getElementById(`receipt-upload-${hash}`);
    if (input) {
      input.click();
    }
  }

  const handleReceiptFileChange = async (hash, event) => {
    const file = event.target.files?.[0];
    event.target.value = null;
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      window.showMessage('Файл должен быть в формате PDF');
      return;
    }
    setIsUploading(true);
    setReceiptHash(null);
    setReceiptStatus(null);

    const response = await REST.uploadReceipt(hash, file);

    if (response && response.success) {
      setReceiptHash(response.data.hash);
      window.showMessage(getText('receiptUploaded'));
      setIsUploading(false);
    } else {
      setIsUploading(false);
      if (response && response.data && response.data[0] && response.data[0].message) {
        window.showMessage(response.data[0].message);
      } else {
        window.showMessage(getText('receiptUploadError'));
      }
    }
  }

  if (canUploadReceipt) {
    let color = receiptStatus === 'error' ? "error" : receiptStatus === 'success' ? "success.main" : "secondary.text"
    return <Box sx={{
      alignSelf: 'center',
      marginLeft: 'auto',
      paddingTop: { xs: '10px', sm: '0px' },
      width: { xs: '100%', sm: 'auto' },
    }}>
      <input
        type="file"
        id={`receipt-upload-${hash}`}
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={(event) => handleReceiptFileChange(hash, event)}
      />
      {((!receiptHash || receiptStatus === 'error') ||
        (receiptHash && receiptStatus == null)) &&
        <DepositPayButtonStyled
          variant="contained"
          onClick={() => openReceiptUpload(hash)}
          disabled={isUploading}
        >
          {isUploading ? getText('receiptUploading') : getText('receiptUpload')}
        </DepositPayButtonStyled>}
      {receiptStatus && receiptStatus !== 'success' &&
        <DepositPayButtonStyled
          variant="contained"
          statusColor={color}
        >
          {statuses[receiptStatus]}
        </DepositPayButtonStyled>
      }
    </Box>
  }

  return <DepositPayButtonStyled
    variant="contained"
    color={canSetPaid ? "primary" : "secondary"}
    disabled={!canSetPaid}
    onClick={setPaid}
  >
    {canSetPaid === true ? getText('payBankPaid') : getText('payBankPaidDisabled')}
  </DepositPayButtonStyled>
}



const PayByBank = ({ order, getStatus, handleTimeOut }) => {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.only('xs'));
  const [loadingPay, setLoadingPay] = React.useState([]);

  const notices = order.directReqsArray?.map(directReqs => directReqs.notice);
  const uniqueNotices = notices?.filter(notice => notices.indexOf(notice) === notices.lastIndexOf(notice));

  const noticeCounts = notices?.reduce((acc, notice) => {
    acc[notice] = (acc[notice] || 0) + 1;
    return acc;
  }, {});
  const nonUniqueNotices = noticeCounts ? Object.keys(noticeCounts).filter(notice => noticeCounts[notice] > 1) : [];

  if (order.directReqsArray?.length == 1) {
    nonUniqueNotices[0] = uniqueNotices[0];
    uniqueNotices[0] = undefined;
  }


  const isSimpleOrder = order.canSetPaid == true || order.canSetPaid == undefined;

  const directReqsList = order.directReqsArray?.map((directReqs) => (
    <Box sx={{ marginBottom: '30px' }}>
      <MyBox
        key={directReqs.id}
        disabled={!isSimpleOrder && (loadingPay.includes(directReqs.hash) || !directReqs.canSetPaid)}
      >
        <Box>
          <PSIcon
            name={
              order.fromPaymentSystemType?.value !== "BANK" ?
                order.fromPaymentSystemClassName :
                directReqs.bank?.icon || getCardType(directReqs.number?.value)
            }
            height={isXS ? 35 : 45}
            width={isXS ? 35 : 45}
            margin={isXS ? '5px 15px 0 0' : '10px 20px 0 0'}
          />
        </Box>
        <Box sx={{ flexGrow: { xs: 1, sm: 0 } }}>
          <Box style={{ display: 'flex', alignItems: 'center', margin: '0 0 3px' }}>
            <Typography variant="orderHashII">
              {formatCardNumber(directReqs.number.value, order.fromPaymentSystemType?.value)}
            </Typography>
            <CClipboard text={directReqs.number?.copyValue}>
              <Box sx={{ margin: { xs: '3px 0 0 10px', sm: '3px 0 0 15px' } }}>
                <CopyIcon width={isXS ? 20 : 32} height={isXS ? 20 : 32} />
              </Box>
            </CClipboard>
          </Box>
          <CardInfo>
            <Stack spacing={1}>
              <Typography variant="regularCaption" sx={{ width: '100%' }}>
                {directReqs.fio?.value}
              </Typography>
              {directReqs.bank && (
                <Typography variant="regularCaption" sx={{ width: '100%' }}>
                  {directReqs.bank?.value}
                </Typography>
              )}
            </Stack>
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: { xs: 14, sm: 20 } }}>
                {directReqs.amount?.value}
              </Typography>
              <CClipboard text={directReqs.amount?.copyValue}>
                <Box sx={{ margin: '4px 0 0 9px' }}>
                  <CopyIcon width={isXS ? 20 : 24} height={isXS ? 20 : 24} />
                </Box>
              </CClipboard>
            </Box>
          </CardInfo>
        </Box>

        {!isSimpleOrder && directReqs.canSetPaid !== undefined && <DepositPayButton
          hash={directReqs.hash}
          canSetPaid={directReqs.canSetPaid}
          canUploadReceipt={directReqs.canUploadReceipt}
          setPaid={() => handleDepositPaid(directReqs.hash)}
        />}

      </MyBox >
      {
        directReqs.notice && uniqueNotices.includes(directReqs.notice) && <Notice
          type="warning"
          icon={<Warning />}
          sx={{ marginTop: '12px' }}
        >
          <p>{directReqs.notice}</p>
        </Notice>
      }
    </Box >
  ));

  const showDialog = () => {
    handlePaid();
  };

  const handlePaid = () => {
    REST.setPaid(order.code).then((data) => {
      if (data.success) {
        getStatus();
      } else {
        window.showMessage(data.data[0].message);
      }
    });
  };

  const handleDepositPaid = (hash) => {
    setLoadingPay(prevLoadingPay => (
      prevLoadingPay.includes(hash) ? prevLoadingPay : [...prevLoadingPay, hash]
    ));
    REST.setDepositPaid(hash).then((data) => {
      if (data.success) {
        setTimeout(() => {
          setLoadingPay(prevLoadingPay => prevLoadingPay.filter(item => item !== hash));
        }, 10000);
        getStatus();
      } else {
        setLoadingPay(prevLoadingPay => prevLoadingPay.filter(item => item !== hash));
        window.showMessage(data.data?.[0]?.message || getText('receiptError'));
      }
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h3">{getText('orderHeaderState9')}</Typography>
        <TimerTile timerEnd={order.endAt} handleTimeOut={handleTimeOut} />
      </Box>

      <Typography variant="regularCaption" component="div">
        {order?.fromPaymentSystemType?.value == "BANK" ? getText('payBankTimerHint') : getText('payBankTimerHintPS')}
      </Typography>

      <MyBox sx={{ marginTop: '30px' }}>
        <Box>
          <PSIcon
            name={order.fromPaymentSystemClassName}
            height={isXS ? 35 : 45}
            width={isXS ? 35 : 45}
            margin={isXS ? '5px 15px 0 0' : '10px 20px 0 0'}
          />
        </Box>
        <Box>
          <Typography variant="orderHashII" component="div" sx={{ margin: '0 0 3px' }}>
            {formatCardNumber(order.requisites.fromNumber, order.fromPaymentSystemType?.value)}
          </Typography>
          <Typography variant="regularCaption">{order.requisites.fromFio}</Typography>
        </Box>
      </MyBox>

      <Box sx={{ textAlign: 'center', margin: '12px 0px' }}><ArrowDownIII /></Box>

      {order.directReqs ? directReqsList : <MyBox
        sx={{
          marginBottom: '30px',
          fontSize: '16px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        }}

      >
        <Box>
          {getText('payBankPPWarn01')} <br />{getText('payBankPPWarn02')}
        </Box>
        <JivoChatButton
          variant="contained"
          size="large"
          sx={{
            height: '50px !important',
            alignSelf: 'center !important',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {getText("payBankBtnChat")}
        </JivoChatButton>
      </MyBox>}

      {nonUniqueNotices?.filter(notice => notice !== "" && notice !== 'null' && notice !== null).map(notice => <Notice
        type="warning"
        icon={<Warning />}
        sx={{ marginTop: '12px' }}
      >
        <p>{notice}</p>
      </Notice>)}

      <BottomAction>
        {order.directReqs?.onlineBank && (
          <Button
            variant="default"
            sx={{ padding: '15px 31px' }}
            onClick={() => window.open(order.directReqs.onlineBank.value, '_blank')}
          >
            {getText("payBankBtnText")}
          </Button>
        )}

        {isSimpleOrder && (
          <Button
            variant="contained"
            sx={{ padding: '15px 31px' }}
            onClick={showDialog}
          >
            {getText('sSubmitPaid')}
          </Button>
        )}
        <CancelOrder size="large" orderCode={order.code} updateCallback={getStatus} />
      </BottomAction>
    </>
  );
};

export default PayByBank
